/**
 * RostersManager — Admin Dashboard "Rosters" tab, extracted from AdminDashboard.jsx
 * during P2 refactor (2026-02-22). Fully self-contained: owns its state, fetches
 * its own data, and exposes one prop (`token`) for authenticated requests.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';
import { Plus, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_TEAM_FORM = { name: '', sport: 'Basketball', coach_name: '', coach_email: '', color: '#00d4ff' };
const EMPTY_PLAYER_FORM = { name: '', jersey_number: '', position: '', grade: '', height: '' };

export default function RostersManager({ token }) {
  const [adminTeams, setAdminTeams] = useState([]);
  const [showAdminTeamDialog, setShowAdminTeamDialog] = useState(false);
  const [showAdminPlayerDialog, setShowAdminPlayerDialog] = useState(false);
  const [selectedAdminTeamId, setSelectedAdminTeamId] = useState(null);
  const [adminTeamForm, setAdminTeamForm] = useState(EMPTY_TEAM_FORM);
  const [adminPlayerForm, setAdminPlayerForm] = useState(EMPTY_PLAYER_FORM);

  const fetchAdminTeams = async () => {
    try {
      const res = await fetch(`${API_URL}/api/teams`);
      if (res.ok) setAdminTeams(await res.json());
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  useEffect(() => {
    fetchAdminTeams();
  }, []);

  const handleCreateAdminTeam = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(adminTeamForm),
      });
      if (res.ok) {
        toast.success('Team created');
        setShowAdminTeamDialog(false);
        setAdminTeamForm(EMPTY_TEAM_FORM);
        fetchAdminTeams();
      } else {
        toast.error('Failed to create team');
      }
    } catch {
      toast.error('Error creating team');
    }
  };

  const handleDeleteAdminTeam = async (teamId) => {
    if (!window.confirm('Delete this team and all its players?')) return;
    try {
      const res = await fetch(`${API_URL}/api/teams/${teamId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Team deleted');
        fetchAdminTeams();
      } else {
        toast.error('Failed to delete team');
      }
    } catch {
      toast.error('Error deleting team');
    }
  };

  const handleAddAdminPlayer = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/teams/${selectedAdminTeamId}/players`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...adminPlayerForm, jersey_number: parseInt(adminPlayerForm.jersey_number) || 0 }),
      });
      if (res.ok) {
        toast.success('Player added');
        setShowAdminPlayerDialog(false);
        setAdminPlayerForm(EMPTY_PLAYER_FORM);
        fetchAdminTeams();
      } else {
        toast.error('Failed to add player');
      }
    } catch {
      toast.error('Error adding player');
    }
  };

  const handleRemoveAdminPlayer = async (teamId, playerId) => {
    if (!window.confirm('Remove this player?')) return;
    try {
      const res = await fetch(`${API_URL}/api/teams/${teamId}/players/${playerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Player removed');
        fetchAdminTeams();
      } else {
        toast.error('Failed to remove player');
      }
    } catch {
      toast.error('Error removing player');
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-5">
        <h2 className="text-xl font-bold text-white">Team Rosters</h2>
        <Dialog open={showAdminTeamDialog} onOpenChange={setShowAdminTeamDialog}>
          <DialogTrigger asChild>
            <Button className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold" data-testid="create-team-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Team
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Team</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAdminTeam} className="space-y-4">
              <div>
                <Label className="text-gray-300">Team Name</Label>
                <Input value={adminTeamForm.name} onChange={(e) => setAdminTeamForm({ ...adminTeamForm, name: e.target.value })} className="bg-rooam-black border-rooam-gray-light text-white" required />
              </div>
              <div>
                <Label className="text-gray-300">Sport</Label>
                <Select value={adminTeamForm.sport} onValueChange={(v) => setAdminTeamForm({ ...adminTeamForm, sport: v })}>
                  <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                    <SelectItem value="Basketball">Basketball</SelectItem>
                    <SelectItem value="Football">Football</SelectItem>
                    <SelectItem value="Soccer">Soccer</SelectItem>
                    <SelectItem value="Volleyball">Volleyball</SelectItem>
                    <SelectItem value="Flag Football">Flag Football</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Coach Name</Label>
                <Input value={adminTeamForm.coach_name} onChange={(e) => setAdminTeamForm({ ...adminTeamForm, coach_name: e.target.value })} className="bg-rooam-black border-rooam-gray-light text-white" required />
              </div>
              <div>
                <Label className="text-gray-300">Coach Email</Label>
                <Input type="email" value={adminTeamForm.coach_email} onChange={(e) => setAdminTeamForm({ ...adminTeamForm, coach_email: e.target.value })} className="bg-rooam-black border-rooam-gray-light text-white" />
              </div>
              <div>
                <Label className="text-gray-300">Team Color</Label>
                <Input type="color" value={adminTeamForm.color} onChange={(e) => setAdminTeamForm({ ...adminTeamForm, color: e.target.value })} className="h-10 cursor-pointer" />
              </div>
              <Button type="submit" className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold">Create Team</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Add Player Dialog */}
      <Dialog open={showAdminPlayerDialog} onOpenChange={setShowAdminPlayerDialog}>
        <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Add Player</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddAdminPlayer} className="space-y-4">
            <div>
              <Label className="text-gray-300">Player Name</Label>
              <Input value={adminPlayerForm.name} onChange={(e) => setAdminPlayerForm({ ...adminPlayerForm, name: e.target.value })} className="bg-rooam-black border-rooam-gray-light text-white" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">Jersey #</Label>
                <Input type="number" value={adminPlayerForm.jersey_number} onChange={(e) => setAdminPlayerForm({ ...adminPlayerForm, jersey_number: e.target.value })} className="bg-rooam-black border-rooam-gray-light text-white" required />
              </div>
              <div>
                <Label className="text-gray-300">Position</Label>
                <Input value={adminPlayerForm.position} onChange={(e) => setAdminPlayerForm({ ...adminPlayerForm, position: e.target.value })} className="bg-rooam-black border-rooam-gray-light text-white" placeholder="PG, SG, SF..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">Grade</Label>
                <Input value={adminPlayerForm.grade} onChange={(e) => setAdminPlayerForm({ ...adminPlayerForm, grade: e.target.value })} className="bg-rooam-black border-rooam-gray-light text-white" placeholder="Freshman, Jr..." />
              </div>
              <div>
                <Label className="text-gray-300">Height</Label>
                <Input value={adminPlayerForm.height} onChange={(e) => setAdminPlayerForm({ ...adminPlayerForm, height: e.target.value })} className="bg-rooam-black border-rooam-gray-light text-white" placeholder="6'2&quot;" />
              </div>
            </div>
            <Button type="submit" className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold">Add Player</Button>
          </form>
        </DialogContent>
      </Dialog>

      {adminTeams.length === 0 ? (
        <Card className="bg-rooam-gray-medium border-rooam-gray-light">
          <CardContent className="p-8 text-center">
            <Users className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Teams Yet</h3>
            <p className="text-sm text-gray-400">Create a team to start managing rosters</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {adminTeams.map((team) => (
            <Card key={team.id} className="bg-rooam-gray-medium border-rooam-gray-light" data-testid={`team-card-${team.id}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: team.color || '#00d4ff' }} />
                  <CardTitle className="text-white text-base truncate">{team.name}</CardTitle>
                  <Badge className="bg-rooam-blue/20 text-rooam-blue border-rooam-blue text-xs shrink-0">{team.sport}</Badge>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="sm" className="text-rooam-blue hover:bg-rooam-blue/10 h-8 w-8 p-0" onClick={() => { setSelectedAdminTeamId(team.id); setShowAdminPlayerDialog(true); }} data-testid={`add-player-${team.id}`}>
                    <Plus className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10 h-8 w-8 p-0" onClick={() => handleDeleteAdminTeam(team.id)} data-testid={`delete-team-${team.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs text-gray-500 mb-3">Coach: {team.coach_name} {team.coach_email ? `(${team.coach_email})` : ''}</p>
                <div className="text-xs font-semibold text-gray-400 mb-2">Players ({team.players?.length || 0})</div>
                {team.players?.length > 0 ? (
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {team.players.map((player) => (
                      <div key={player.id} className="flex items-center justify-between bg-rooam-black rounded-lg px-3 py-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-rooam-blue font-bold text-sm w-8 text-center shrink-0">#{player.jersey_number}</span>
                          <div className="min-w-0">
                            <p className="text-white text-sm truncate">{player.name}</p>
                            <p className="text-gray-500 text-[10px]">
                              {[player.position, player.grade, player.height].filter(Boolean).join(' | ')}
                            </p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10 h-7 w-7 p-0 shrink-0" onClick={() => handleRemoveAdminPlayer(team.id, player.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-600 py-3 text-center">No players yet — tap + to add</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
